import type { IMenuSections, IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { DEFAULT_MENU_ITEM } from './buildMenu';

/** Розділи сторінки налаштувань модуля, які приймає маршрут …/settings/:item (підписи — i18n рушія `settings.<ключ>`). */
export const settingsItems: readonly string[] = ['fields', 'form', 'records', 'statuses', 'types'];

/**
 * Пункти секції «Налаштування» лівого меню (прототип v4: «Поля й подання», «Форма», «Записи й форми»; далі — довідники модуля,
 * catalogueLinksOf). Статуси й типи модуля в меню — лише його довідниками, як класифікатори прототипу (setCls).
 */
export const settingsMenuItems: readonly string[] = ['fields', 'form', 'records'];

/**
 * Пункти секції «Проєктування» лівого меню в порядку прототипу v4 (mdItems): функції, мотивація, зацікавлені сторони, шляхи користувачів,
 * сценарії використання, вимоги, модель даних (підписи — i18n рушія `design.<ключ>`).
 */
export const designItems: readonly string[] = ['features', 'motivation', 'stakeholders', 'journeys', 'useCases', 'requirements', 'model'];

/** Які стандартні секції меню показувати: з визначення, типово — усі. */
export function menuSectionsOf(definition: IModuleDefinition): IMenuSections {
  return definition.menuSections ?? { pages: true, planning: true, tables: true, settings: true };
}

/** Головний тип запису модуля (main), інакше перший. */
export function mainRecordTypeOf(definition: IModuleDefinition): IRecordTypeDefinition | undefined {
  return definition.recordTypes.find((r) => r.main) ?? definition.recordTypes[0];
}

/** Порядок таблиць модуля для секції «Таблиці»: головна, дочірні таблиці модуля, довідники — у порядку визначення всередині групи; без прихованих типів. */
export function tablesOf(definition: IModuleDefinition): IRecordTypeDefinition[] {
  const rank = (rt: IRecordTypeDefinition): number => (rt.main || rt.kind === 'main' ? 0 : rt.kind === 'catalogue' ? 2 : 1);
  return definition.recordTypes.filter((rt) => !isHiddenType(rt)).sort((a, b) => rank(a) - rank(b));
}

/** Група меню «Планування» (англійська назва групи визначення — ключ розділу buildMenu): спринти й незаплановане. */
export const PLANNING_GROUP = 'Planning';

/** Тип запису з групами меню для рушія: без секції «Планування» група `Planning` прибирається (menuSections.planning). */
export function menuGroupsFor(rt: IRecordTypeDefinition, planning: boolean): IRecordTypeDefinition {
  return planning ? rt : { ...rt, menu: rt.menu.filter((g) => g.group.en !== PLANNING_GROUP) };
}

export function isDesigned(rt: IRecordTypeDefinition): boolean {
  return rt.status === 'designed';
}

/** Тип, який API віддає, але загальний інтерфейс не перелічує (`hidden`: дані бібліотек бази знань) — його немає в розділах, «Таблицях» і довідниках. */
export function isHiddenType(rt: IRecordTypeDefinition): boolean {
  return rt.hidden === true;
}

/** Вид типу запису з визначення; без `kind` — головний за `main`, інакше таблиця модуля. */
export function kindOf(rt: IRecordTypeDefinition): 'main' | 'child' | 'catalogue' {
  return rt.kind ?? (rt.main ? 'main' : 'child');
}

/**
 * Типи записів із власним розгортним розділом меню (прототип v4): головний і реалізовані дочірні таблиці модуля в порядку визначення
 * (Верховна Рада: мандати, скликання, партії, фракції, помічники). Без реалізованих дочірніх — порожньо: тоді меню показує групи головного типу.
 */
export function sectionTypesOf(definition: IModuleDefinition): IRecordTypeDefinition[] {
  const main = mainRecordTypeOf(definition);
  if (!main) return [];
  const kids = definition.recordTypes.filter((rt) => rt.key !== main.key && kindOf(rt) === 'child' && !isDesigned(rt) && !isHiddenType(rt));
  return kids.length ? [main, ...kids] : [];
}

/** Таблиці секції «Таблиці»: усі таблиці модуля в порядку tablesOf, крім типів із власним розділом (sectionTypesOf). */
export function tableTypesOf(definition: IModuleDefinition, sectionTypes: IRecordTypeDefinition[]): IRecordTypeDefinition[] {
  const own = new Set(sectionTypes.map((rt) => rt.key));
  return tablesOf(definition).filter((rt) => !own.has(rt.key));
}

/** Ключ пункту «Усі записи» типу в меню (buildMenu): `view:table`, якщо меню має пункт таблиці, інакше типовий DEFAULT_MENU_ITEM. */
export function tableEntryKeyOf(rt: IRecordTypeDefinition): string {
  return rt.menu.some((g) => g.items?.some((it) => it.view === 'table')) ? 'view:table' : DEFAULT_MENU_ITEM;
}
